import { modelPortfolioDeviates } from "@repo/shared";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePublicSession } from "@/lib/context/public-session-context";
import { useModelPortfolioCandidates } from "@/lib/hooks/use-flow-instances/use-portfolios";
import { usePublicModelPortfolioCandidates } from "@/lib/hooks/use-public-flow/use-portfolios";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./model-portfolio-select.i18n";

/** Radix Select has no empty-value item; this stands for Automatic (null). */
const AUTO = "__auto__";

type ModelPortfolioSelectProps = {
  portfolioId: string;
  goalRiskScore: number | null;
  platformId: string | null;
  disabled?: boolean;
  /** Pin a model portfolio by hand, or `null` for Automatic. */
  onChange: (pickedPortfolioId: string | null) => void;
  /**
   * A pick waiting for the parent's delayed save: shown instead of the saved
   * pick until the save lands. `undefined` = nothing pending, `null` = Automatic.
   */
  pendingValue?: string | null;
};

/**
 * QL-72 — model-portfolio picker under the portfolio type select. "Automatic"
 * comes first and follows the goal's risk score (it names the portfolio it
 * resolves to); every eligible portfolio follows, the matching one marked, and
 * choosing any of them — the matching one included — pins it. Shown on a model
 * portfolio when the collection allows it or the portfolio still holds a pick;
 * the server enforces both. Renders nothing while the candidates load or when
 * the server refuses. Disabled, with a hint, until the goal has a risk score.
 * With the collection setting off, a kept pick still renders: the server
 * narrows the list to the pick, and a hint says only Automatic is possible.
 */
export function ModelPortfolioSelect({
  portfolioId,
  goalRiskScore,
  platformId,
  disabled = false,
  onChange,
  pendingValue,
}: ModelPortfolioSelectProps) {
  const { isPublic } = usePublicSession();
  const t = useT(T);
  const { userLanguage } = useUserLanguage();
  // One query per surface; only the one for the current surface is enabled.
  const advisorQuery = useModelPortfolioCandidates(portfolioId, {
    enabled: !isPublic,
    goalRiskScore,
    platformId,
  });
  const publicQuery = usePublicModelPortfolioCandidates(portfolioId, {
    goalRiskScore,
    platformId,
    lang: userLanguage,
  });
  const data = isPublic ? publicQuery.data : advisorQuery.data;
  if (!data) return null;

  const savedPick = data.pickedPortfolioId;
  const shownPick = pendingValue !== undefined ? pendingValue : savedPick;
  const value = shownPick ?? AUTO;
  // A pick needs the goal's risk score; the server refuses one before that.
  // A portfolio still holding a pick can always return to Automatic.
  const awaitingScore = data.goalRiskScore == null && savedPick == null;
  const calculated = data.candidates.find((c) => c.id === data.calculatedPortfolioId);
  const automaticLabel =
    data.goalRiskScore != null && data.calculatedPortfolioId == null
      ? t.automaticNoMatch(data.goalRiskScore)
      : t.automatic({ score: data.goalRiskScore, name: calculated?.name ?? null });
  const picked = data.candidates.find((c) => c.id === savedPick);
  const deviates =
    picked != null &&
    modelPortfolioDeviates({
      calculatedPortfolioId: data.calculatedPortfolioId,
      pickedPortfolioId: picked.id,
    });

  return (
    <div className="space-y-2">
      <Label htmlFor={`modelPortfolio-${portfolioId}`}>{t.label}</Label>
      <Select
        value={value}
        onValueChange={(next) => {
          const pick = next === AUTO ? null : next;
          if (pick === shownPick) return;
          onChange(pick);
        }}
        disabled={disabled || awaitingScore}
      >
        <SelectTrigger id={`modelPortfolio-${portfolioId}`}>
          <SelectValue placeholder={t.choose} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={AUTO}>{automaticLabel}</SelectItem>
          {data.candidates.map((c) => (
            <SelectItem key={c.id} value={c.id}>
              <span>{t.item({ name: c.name, score: c.riskScore })}</span>
              {c.isMatching && <span className="text-muted-foreground"> — {t.matching}</span>}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {awaitingScore && <p className="text-muted-foreground text-xs">{t.noRiskScore}</p>}
      {!data.selectionAllowed && <p className="text-muted-foreground text-xs">{t.selectionOff}</p>}
      {deviates && data.goalRiskScore != null && (
        <Badge variant="warning">
          {t.deviates({ goal: data.goalRiskScore, portfolio: picked.riskScore })}
        </Badge>
      )}
    </div>
  );
}
